// src/pages/tickets/CreateTicket.jsx - Windows-Style Hierarchical Category Picker - COMPLETE
import React, { useState, useRef, useEffect } from 'react';
import { 
  ArrowLeft, 
  X, 
  Upload,
  FileText,
  Image as ImageIcon,
  AlertCircle,
  CheckCircle,
  Info,
  HelpCircle,
  Loader2,
  Search,
  Users,
  File,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import TicketService from '../../services/ticketService';
import api, { handleApiResponse, handleApiError } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

// CategoryService with hierarchy support
const CategoryService = {
  async getCategories() {
    try {
      const response = await api.get('/tickets/categories');
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async getCategoryTree() {
    try {
      const response = await api.get('/categories/tree');
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }
};

// Helper functions
function findCategoryById(categories, categoryId) {
  if (!categoryId || !categories) return null;
  
  for (const category of categories) {
    if (category.id === parseInt(categoryId)) {
      return category;
    }
    if (category.children) {
      const found = findCategoryById(category.children, categoryId);
      if (found) return found;
    }
  }
  return null;
}

function getCategoryPath(categories, categoryId) {
  if (!categoryId || !categories) return [];
  
  function findPath(cats, targetId, path = []) {
    for (const cat of cats) {
      const currentPath = [...path, cat];
      if (cat.id === parseInt(targetId)) {
        return currentPath;
      }
      if (cat.children) {
        const found = findPath(cat.children, targetId, currentPath);
        if (found) return found;
      }
    }
    return null;
  }
  
  return findPath(categories, categoryId) || [];
}

// Hierarchical Category Picker Component
const HierarchicalCategoryPicker = ({ categories, selectedCategoryId, onCategorySelect, isSubmitting, validationError }) => {
  const [openMenus, setOpenMenus] = useState(new Set());
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);

  const selectedCategory = findCategoryById(categories, selectedCategoryId);
  const selectedPath = selectedCategory ? getCategoryPath(categories, selectedCategoryId) : [];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setOpenMenus(new Set());
        setHoveredItem(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleMouseEnter = (categoryId) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setHoveredItem(categoryId);
    
    const category = findCategoryById(categories, categoryId);
    if (category && category.children && category.children.length > 0) {
      setOpenMenus(prev => new Set([...prev, categoryId]));
    }
  };

  const handleMouseLeave = (categoryId) => {
    timeoutRef.current = setTimeout(() => {
      setHoveredItem(null);
      setOpenMenus(prev => {
        const newSet = new Set(prev);
        newSet.delete(categoryId);
        return newSet;
      });
    }, 200); // Small delay to allow moving to submenu
  };

  const handleCategoryClick = (category) => {
    // Only allow selection of leaf categories (categories without children)
    if (!category.children || category.children.length === 0) {
      onCategorySelect(category);
      setIsOpen(false);
      setOpenMenus(new Set());
      setHoveredItem(null);
    }
  };

  const renderCategoryMenu = (categoriesList, level = 0, parentId = null) => {
    return (
      <div 
        className={`${level === 0 ? 'w-full' : 'absolute left-full top-0 min-w-[250px]'} bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50`}
        style={{ marginLeft: level > 0 ? '2px' : '0' }}
      >
        {categoriesList.map((category) => {
          const hasChildren = category.children && category.children.length > 0;
          const isSelectable = !hasChildren;
          const isHovered = hoveredItem === category.id;
          const isMenuOpen = openMenus.has(category.id);

          return (
            <div
              key={category.id}
              className="relative"
              onMouseEnter={() => handleMouseEnter(category.id)}
              onMouseLeave={() => handleMouseLeave(category.id)}
            >
              <div
                className={`flex items-center justify-between px-3 py-2 text-sm cursor-pointer transition-colors ${
                  isHovered ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                } ${!isSelectable ? 'font-medium' : ''}`}
                onClick={() => handleCategoryClick(category)}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {hasChildren ? (
                    <FolderOpen className="h-4 w-4 text-blue-500 flex-shrink-0" />
                  ) : (
                    <File className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  )}
                  <span className="truncate" title={category.name}>
                    {category.name}
                  </span>
                </div>
                
                <div className="flex items-center gap-1 flex-shrink-0">
                  {!isSelectable && (
                    <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                      {category.children?.length} items
                    </span>
                  )}
                  {hasChildren && (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </div>
              </div>
              
              {/* Submenu */}
              {hasChildren && isMenuOpen && (
                <div className="absolute left-full top-0 z-50">
                  {renderCategoryMenu(category.children, level + 1, category.id)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Category <span className="text-red-500">*</span>
      </label>
      
      {/* Selected Category Display / Trigger */}
      <div
        className={`w-full px-3 py-2 border rounded-lg cursor-pointer transition-colors ${
          validationError ? 'border-red-300' : 'border-gray-300 hover:border-blue-400'
        } ${isOpen ? 'ring-2 ring-blue-500 border-blue-500' : ''}`}
        onClick={() => !isSubmitting && setIsOpen(!isOpen)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {selectedCategory ? (
              <>
                <File className="h-4 w-4 text-green-500 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {selectedCategory.name}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {selectedPath.map(cat => cat.name).join(' → ')}
                  </div>
                </div>
              </>
            ) : (
              <>
                <Folder className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="text-gray-500">Select a category</span>
              </>
            )}
          </div>
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </div>

      {validationError && (
        <p className="mt-1 text-sm text-red-600">{validationError}</p>
      )}

      {/* Dropdown Menu */}
      {isOpen && !isSubmitting && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50">
          {renderCategoryMenu(categories)}
        </div>
      )}

      {/* Selected Category Details */}
      {selectedCategory && (
        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-green-900">Selected:</span>
              <span className="text-sm text-green-800 font-medium">{selectedCategory.name}</span>
            </div>
            
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-green-900">Path:</span>
              <div className="text-sm text-green-700">
                {selectedPath.map((cat, index) => (
                  <span key={cat.id}>
                    {index > 0 && <span className="mx-1 text-green-500">→</span>}
                    {cat.name}
                  </span>
                ))}
              </div>
            </div>
            
            {selectedCategory.description && (
              <div className="border-t border-green-200 pt-2">
                <div className="flex items-start gap-2">
                  <span className="text-sm font-medium text-green-900">Description:</span>
                  <p className="text-sm text-green-700 flex-1">
                    {selectedCategory.description}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Help Text */}
      <div className="mt-2 text-xs text-gray-500">
        <div className="flex items-start gap-1">
          <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
          <div>
            Navigate through categories using hover. Only final categories (without subcategories) can be selected.
          </div>
        </div>
      </div>
    </div>
  );
};

const CreateTicket = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const { user, isAgent, hasPermission } = useAuth();
  
  const urlMode = searchParams.get('mode');
  const canCreateOnBehalf = isAgent() && hasPermission('tickets.create-behalf');
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  
  // Category management
  const [categoryTree, setCategoryTree] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [categoriesError, setCategoriesError] = useState(null);
  const [createdTicketId, setCreatedTicketId] = useState(null);

  // On-behalf functionality
  const [isOnBehalfMode, setIsOnBehalfMode] = useState(urlMode === 'on-behalf' || (canCreateOnBehalf && !isCustomerUser()));
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [isSearchingCustomers, setIsSearchingCustomers] = useState(false);
  const [customerSearchFocused, setCustomerSearchFocused] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category_id: '',
    priority: 'medium',
    affecting_multiple_users: false,
    blocking_work: false,
    follow_up_call: false,
    creator_id: '',
    reason_for_behalf: ''
  });

  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [validationErrors, setValidationErrors] = useState({});
  const [isDragging, setIsDragging] = useState(false);

  function isCustomerUser() {
    return !isAgent() && user && !user.is_admin && !user.is_customer_care;
  }

  const searchCustomers = async (searchTerm) => {
    if (!searchTerm.trim() || searchTerm.length < 2) {
      setCustomerSearchResults([]);
      return;
    }

    try {
      setIsSearchingCustomers(true);
      const response = await TicketService.searchCustomers(searchTerm);
      
      let customers = [];
      if (response?.data?.customers) {
        customers = response.data.customers;
      } else if (response?.customers) {
        customers = response.customers;
      } else if (Array.isArray(response?.data)) {
        customers = response.data;
      } else if (Array.isArray(response)) {
        customers = response;
      }
      
      setCustomerSearchResults(customers);
    } catch (error) {
      console.error('Customer search error:', error);
      setCustomerSearchResults([]);
    } finally {
      setIsSearchingCustomers(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (isOnBehalfMode && customerSearch && customerSearchFocused) {
        searchCustomers(customerSearch);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [customerSearch, isOnBehalfMode, customerSearchFocused]);

  // Load categories with hierarchy
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setIsLoadingCategories(true);
        setCategoriesError(null);
        setError(null);
        
        let categoriesData = [];
        
        try {
          const response = await CategoryService.getCategoryTree();
          
          if (response?.data?.categories) {
            categoriesData = response.data.categories;
          } else if (response?.data?.data) {
            categoriesData = response.data.data;
          } else if (Array.isArray(response?.data)) {
            categoriesData = response.data;
          } else if (Array.isArray(response)) {
            categoriesData = response;
          }
          
          setCategoryTree(categoriesData);
          
        } catch (treeError) {
          console.warn('Category tree not available, falling back to simple categories:', treeError);
          
          const response = await CategoryService.getCategories();
          
          if (response?.data?.data) {
            categoriesData = response.data.data;
          } else if (response?.data) {
            categoriesData = Array.isArray(response.data) ? response.data : [response.data];
          } else if (Array.isArray(response)) {
            categoriesData = response;
          }
          
          // Convert flat categories to tree structure
          setCategoryTree(categoriesData.map(cat => ({
            ...cat,
            children: []
          })));
        }
        
        if (categoriesData.length === 0) {
          throw new Error('No categories available');
        }
        
      } catch (err) {
        console.error('Failed to load categories:', err);
        const errorMessage = err.message || 'Failed to load categories';
        setCategoriesError(errorMessage);
        setError(`Unable to load form: ${errorMessage}`);
        setCategoryTree([]);
      } finally {
        setIsLoadingCategories(false);
      }
    };

    loadCategories();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }

    if (error) {
      setError(null);
    }
  };

  const handleCategorySelect = (category) => {
    setFormData(prev => ({
      ...prev,
      category_id: category.id.toString()
    }));

    if (validationErrors.category_id) {
      setValidationErrors(prev => ({
        ...prev,
        category_id: null
      }));
    }
  };

  const handleFileSelect = (files) => {
    const fileList = files ? Array.from(files) : [];
    
    fileList.forEach(file => {
      const validation = TicketService.validateFile(file);
      if (!validation.isValid) {
        setError(validation.errors.join('. '));
        return;
      }

      const timestamp = Date.now();
      const randomString = Math.random().toString(36).substring(2, 8);
      
      const fileInfo = {
        name: file.name,
        size: file.size,
        type: file.type,
        file: file,
        id: `${timestamp}_${randomString}`,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
      };

      setUploadedFiles(prev => [...prev, fileInfo]);
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileInputChange = (e) => {
    handleFileSelect(e.target.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const removeFile = (index) => {
    const fileToRemove = uploadedFiles[index];
    
    if (fileToRemove.preview) {
      URL.revokeObjectURL(fileToRemove.preview);
    }

    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    } else if (formData.title.trim().length < 5) {
      errors.title = 'Title must be at least 5 characters';
    } else if (formData.title.trim().length > 255) {
      errors.title = 'Title must be less than 255 characters';
    }

    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    } else if (formData.description.trim().length < 20) {
      errors.description = 'Description must be at least 20 characters';
    } else if (formData.description.trim().length > 5000) {
      errors.description = 'Description must be less than 5000 characters';
    }

    if (!formData.category_id) {
      errors.category_id = 'Please select a category';
    }

    if (uploadedFiles.length > 5) {
      errors.attachments = 'Maximum 5 files allowed';
    }

    if (isOnBehalfMode) {
      if (!selectedCustomer) {
        errors.customer = 'Please select a customer';
      }
      if (!formData.reason_for_behalf.trim()) {
        errors.reason_for_behalf = 'Please provide a reason for creating this ticket on behalf of the customer';
      } else if (formData.reason_for_behalf.trim().length < 10) {
        errors.reason_for_behalf = 'Reason must be at least 10 characters';
      } else if (formData.reason_for_behalf.trim().length > 500) {
        errors.reason_for_behalf = 'Reason must be less than 500 characters';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      setError('Please fix the errors below');
      return;
    }

    const formDataToSubmit = new FormData();
    
    formDataToSubmit.append('title', formData.title.trim());
    formDataToSubmit.append('description', formData.description.trim());
    formDataToSubmit.append('category_id', formData.category_id);
    formDataToSubmit.append('priority', formData.priority);

    if (isOnBehalfMode && selectedCustomer) {
      formDataToSubmit.append('creator_id', selectedCustomer.id);
      formDataToSubmit.append('reason_for_behalf', formData.reason_for_behalf.trim());
    }

    uploadedFiles.forEach((fileInfo, index) => {
      formDataToSubmit.append(`attachments[${index}]`, fileInfo.file);
    });

    if (formData.affecting_multiple_users) {
      formDataToSubmit.append('affecting_multiple_users', 'true');
    }
    if (formData.blocking_work) {
      formDataToSubmit.append('blocking_work', 'true');
    }
    if (formData.follow_up_call) {
      formDataToSubmit.append('follow_up_call', 'true');
    }

    setIsSubmitting(true);
    
    try {
      let response;
      if (isOnBehalfMode && selectedCustomer) {
        response = await api.post('/tickets/on-behalf', formDataToSubmit, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        response = await api.post('/tickets', formDataToSubmit, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      
      const result = handleApiResponse(response);
      
      let ticketId = null;
      if (result?.data?.ticket?.id) {
        ticketId = result.data.ticket.id;
      } else if (result?.ticket?.id) {
        ticketId = result.ticket.id;
      } else if (result?.data?.id) {
        ticketId = result.data.id;
      } else if (result?.id) {
        ticketId = result.id;
      }

      setCreatedTicketId(ticketId);
      setSuccess(true);
      
      uploadedFiles.forEach(file => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
      
      setTimeout(() => {
        if (ticketId) {
          navigate(`/tickets/${ticketId}`);
        } else {
          navigate('/tickets');
        }
      }, 2500);
      
      const successMessage = isOnBehalfMode 
        ? `Ticket created successfully on behalf of ${selectedCustomer?.name}!`
        : 'Ticket created successfully!';
      
      toast.success(successMessage, { duration: 3000 });
      
    } catch (err) {
      console.error('Failed to create ticket:', err);
      
      let errorMessage = 'Failed to create ticket. Please try again.';
      
      if (err.response?.status === 422) {
        const validationErrors = err.response.data.errors || {};
        const errorMessages = Object.values(validationErrors).flat();
        errorMessage = `Validation failed: ${errorMessages.join(', ')}`;
      } else if (err.response?.status === 413) {
        errorMessage = 'One or more files are too large. Please reduce file sizes and try again.';
      } else if (err.response?.status === 415) {
        errorMessage = 'One or more file types are not supported. Please use images, PDFs, or office documents.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      toast.error(errorMessage, { duration: 5000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFileIcon = (type) => {
    if (type?.startsWith('image/')) return <ImageIcon className="h-5 w-5 text-blue-500" />;
    if (type?.includes('pdf')) return <FileText className="h-5 w-5 text-red-500" />;
    return <File className="h-5 w-5 text-gray-500" />;
  };

  const formatFileSize = (bytes) => {
    return TicketService.formatFileSize(bytes);
  };

  const navigateBack = () => {
    navigate('/tickets');
  };

  const clearForm = () => {
    setFormData({
      title: '',
      description: '',
      category_id: '',
      priority: 'medium',
      affecting_multiple_users: false,
      blocking_work: false,
      follow_up_call: false,
      creator_id: '',
      reason_for_behalf: ''
    });
    
    uploadedFiles.forEach(file => {
      if (file.preview) {
        URL.revokeObjectURL(file.preview);
      }
    });
    
    setUploadedFiles([]);
    setValidationErrors({});
    setError(null);
    setSelectedCustomer(null);
    setCustomerSearch('');
    setCustomerSearchResults([]);
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch(customer.name);
    setCustomerSearchResults([]);
    setCustomerSearchFocused(false);
    setFormData(prev => ({
      ...prev,
      creator_id: customer.id
    }));
    
    if (validationErrors.customer) {
      setValidationErrors(prev => ({
        ...prev,
        customer: null
      }));
    }
  };

  const clearCustomerSelection = () => {
    setSelectedCustomer(null);
    setCustomerSearch('');
    setCustomerSearchResults([]);
    setCustomerSearchFocused(false);
    setFormData(prev => ({
      ...prev,
      creator_id: '',
      reason_for_behalf: ''
    }));
  };

  const toggleOnBehalfMode = (enabled) => {
    setIsOnBehalfMode(enabled);
    if (!enabled) {
      clearCustomerSelection();
    }
  };

  if (isLoadingCategories) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto" />
            <p className="mt-2 text-sm text-gray-500">Loading categories...</p>
          </div>
        </div>
      </div>
    );
  }

  if (categoriesError) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-red-900 mb-2">Unable to Load Form</h2>
          <p className="text-red-700 mb-4">{categoriesError}</p>
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={navigateBack}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (categoryTree.length === 0 && !isLoadingCategories) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 text-center">
          <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-yellow-900 mb-2">No Categories Available</h2>
          <p className="text-yellow-700 mb-4">There are no ticket categories configured. Please contact your administrator.</p>
          <button onClick={navigateBack} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-green-900 mb-2">Ticket Created Successfully!</h2>
          <p className="text-green-700 mb-2">
            {isOnBehalfMode 
              ? `Support ticket has been created on behalf of ${selectedCustomer?.name} and will be reviewed by our team shortly.`
              : 'Your support ticket has been created and will be reviewed by our team shortly.'
            }
          </p>
          {createdTicketId && (
            <p className="text-sm text-green-600 font-medium">Ticket ID: #{createdTicketId}</p>
          )}
          <p className="text-sm text-green-600 mt-2">
            <Loader2 className="h-4 w-4 animate-spin inline mr-1" />
            Redirecting to your ticket...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button onClick={navigateBack} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" disabled={isSubmitting}>
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isOnBehalfMode ? 'Create Ticket on Behalf' : 'Create New Ticket'}
            </h1>
            <p className="text-sm text-gray-500">
              {isOnBehalfMode 
                ? 'Create a support ticket for a customer'
                : 'Describe your issue and we\'ll help you resolve it'
              }
            </p>
          </div>
        </div>
        <button onClick={() => setShowHelp(!showHelp)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <HelpCircle className="h-5 w-5 text-gray-500" />
        </button>
      </div>

      {/* Help Section */}
      {showHelp && (
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-900 mb-2">Tips for Creating a Great Ticket</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Use a clear, descriptive title that summarizes the issue</li>
            <li>• Include all relevant details in the description</li>
            <li>• Attach screenshots or error messages if available</li>
            <li>• Navigate through categories by hovering - only final categories can be selected</li>
            <li>• Mention any troubleshooting steps you've already tried</li>
            {isOnBehalfMode && (
              <li>• Ensure you have the customer's permission to create this ticket</li>
            )}
          </ul>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium">Error</p>
            <p className="text-sm">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* On-Behalf Toggle for Agents */}
      {canCreateOnBehalf && (
        <div className="mb-6 bg-indigo-50 border border-indigo-200 rounded-lg p-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={isOnBehalfMode}
              onChange={(e) => toggleOnBehalfMode(e.target.checked)}
              disabled={isSubmitting}
              className="rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500"
            />
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-indigo-600" />
              <div>
                <span className="text-sm font-medium text-indigo-900">Create ticket on behalf of customer</span>
                <p className="text-xs text-indigo-700 mt-0.5">Use this option to create a ticket for a customer who contacted you directly</p>
              </div>
            </div>
          </label>
        </div>
      )}

      {/* Customer Selection (when on-behalf mode is active) */}
      {isOnBehalfMode && (
        <div className="mb-6 bg-gray-50 rounded-lg p-4 space-y-4">
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Customer <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                {isSearchingCustomers ? (
                  <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                ) : (
                  <Search className="h-4 w-4 text-gray-400" />
                )}
              </div>
              <input
                type="text"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                onFocus={() => setCustomerSearchFocused(true)}
                onBlur={() => setTimeout(() => setCustomerSearchFocused(false), 200)}
                placeholder="Enter customer ID, name, email, or company"
                className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  validationErrors.customer ? 'border-red-300' : 'border-gray-300'
                }`}
                disabled={isSubmitting || selectedCustomer}
                data-gramm="false"
                data-gramm_editor="false"
                data-enable-grammarly="false"
              />
            </div>
            {validationErrors.customer && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.customer}</p>
            )}
            
            {/* Search Results */}
            {customerSearchResults.length > 0 && customerSearchFocused && !selectedCustomer && (
              <div className="absolute z-10 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {customerSearchResults.map(customer => (
                  <button
                    key={customer.id}
                    type="button"
                    onClick={() => handleCustomerSelect(customer)}
                    className="w-full p-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="font-medium text-gray-900">{customer.name}</div>
                          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                            ID: {customer.id}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">{customer.email}</div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                          {customer.company && (
                            <>
                              <span>{customer.company}</span>
                              {customer.department && <span>• {customer.department}</span>}
                            </>
                          )}
                          {customer.phone && (
                            <span className="ml-auto">{customer.phone}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* No Results Message */}
            {customerSearch.length >= 2 && customerSearchResults.length === 0 && !isSearchingCustomers && !selectedCustomer && customerSearchFocused && (
              <div className="absolute z-10 mt-2 w-full text-sm text-gray-500 p-3 bg-white border border-gray-200 rounded-lg shadow-sm">
                No customers found matching "{customerSearch}"
              </div>
            )}
          </div>

          {/* Selected Customer */}
          {selectedCustomer && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <h4 className="text-sm font-medium text-green-900">Selected Customer</h4>
                <button
                  type="button"
                  onClick={clearCustomerSelection}
                  disabled={isSubmitting}
                  className="text-green-600 hover:text-green-800 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <span className="text-xs text-green-600 font-medium">Customer ID</span>
                    <div className="text-sm text-green-900">
                      {selectedCustomer.user_id_number || `#${selectedCustomer.id}`}
                    </div>
                  </div>
                  
                  <div>
                    <span className="text-xs text-green-600 font-medium">Full Name</span>
                    <div className="text-sm text-green-900">{selectedCustomer.name}</div>
                  </div>
                  
                  <div>
                    <span className="text-xs text-green-600 font-medium">Email Address</span>
                    <div className="text-sm text-green-900">{selectedCustomer.email}</div>
                  </div>
                  
                  <div>
                    <span className="text-xs text-green-600 font-medium">Phone Number</span>
                    <div className="text-sm text-green-900">{selectedCustomer.phone || 'N/A'}</div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <span className="text-xs text-green-600 font-medium">Company</span>
                    <div className="text-sm text-green-900">{selectedCustomer.company || 'N/A'}</div>
                  </div>
                  
                  <div>
                    <span className="text-xs text-green-600 font-medium">Department</span>
                    <div className="text-sm text-green-900">{selectedCustomer.department || 'N/A'}</div>
                  </div>
                  
                  <div>
                    <span className="text-xs text-green-600 font-medium">Total Tickets</span>
                    <div className="text-sm text-green-900">
                      {selectedCustomer.total_tickets || 0}
                    </div>
                  </div>
                  
                  <div>
                    <span className="text-xs text-green-600 font-medium">Status</span>
                    <div className={`text-sm font-medium ${selectedCustomer.is_active ? 'text-green-700' : 'text-red-600'}`}>
                      {selectedCustomer.is_active ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Reason for On-Behalf */}
          {selectedCustomer && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Creating on Behalf <span className="text-red-500">*</span>
              </label>
              <textarea
                name="reason_for_behalf"
                value={formData.reason_for_behalf}
                onChange={handleInputChange}
                rows={3}
                placeholder="Explain why you are creating this ticket on behalf of the customer (e.g., phone request, email forward, in-person visit)"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical ${
                  validationErrors.reason_for_behalf ? 'border-red-300' : 'border-gray-300'
                }`}
                required={isOnBehalfMode}
                disabled={isSubmitting}
                maxLength={500}
                data-gramm="false"
                data-gramm_editor="false"
                data-enable-grammarly="false"
              />
              {validationErrors.reason_for_behalf && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.reason_for_behalf}</p>
              )}
              <div className="mt-1 flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  This helps track the origin of the ticket and maintain audit trail
                </p>
                <p className="text-xs text-gray-500">
                  {formData.reason_for_behalf.length}/500 characters
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Main Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Title */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Brief description of the issue"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  validationErrors.title ? 'border-red-300' : 'border-gray-300'
                }`}
                required
                disabled={isSubmitting}
                maxLength={255}
                data-gramm="false"
                data-gramm_editor="false"
                data-enable-grammarly="false"
              />
              {validationErrors.title && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.title}</p>
              )}
              <div className="mt-1 flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  Example: "Cannot login to email account" or "Printer showing error code E-202"
                </p>
                <p className="text-xs text-gray-500">
                  {formData.title.length}/255 characters
                </p>
              </div>
            </div>

            {/* Priority - Show for agents, hide for customers */}
            {isAgent() && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority <span className="text-red-500">*</span>
                </label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  disabled={isSubmitting}
                >
                  {TicketService.getPriorityLevels().map(priority => (
                    <option key={priority.value} value={priority.value}>
                      {priority.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Select appropriate priority based on business impact
                </p>
              </div>
            )}

            {/* Hierarchical Category Picker */}
            <div className={isAgent() ? '' : 'md:col-span-2'}>
              <HierarchicalCategoryPicker
                categories={categoryTree}
                selectedCategoryId={formData.category_id}
                onCategorySelect={handleCategorySelect}
                isSubmitting={isSubmitting}
                validationError={validationErrors.category_id}
              />
            </div>

            {/* Priority Notice for Customers */}
            {!isAgent() && (
              <div className={isAgent() ? '' : 'md:col-span-2'}>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-medium text-blue-900">Priority Assignment</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        Our support team will assess and assign the appropriate priority based on the impact and urgency of your issue.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Description */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={8}
                placeholder={isOnBehalfMode 
                  ? `Provide detailed information about the customer's issue including:
- What the customer was trying to do
- What actually happened
- Any error messages they saw
- Steps to reproduce the issue
- When the issue started
- Any troubleshooting steps attempted`
                  : `Please provide detailed information about the issue including:
- What you were trying to do
- What actually happened
- Any error messages you saw
- Steps to reproduce the issue
- When the issue started
- Any troubleshooting steps you've already tried`}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-vertical ${
                  validationErrors.description ? 'border-red-300' : 'border-gray-300'
                }`}
                required
                disabled={isSubmitting}
                maxLength={5000}
                data-gramm="false"
                data-gramm_editor="false"
                data-enable-grammarly="false"
              />
              {validationErrors.description && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.description}</p>
              )}
              <div className="mt-1 flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  The more details you provide, the faster we can resolve the issue
                </p>
                <p className="text-xs text-gray-500">
                  {formData.description.length}/5000 characters
                </p>
              </div>
            </div>

            {/* File Attachments */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attachments
                <span className="text-xs font-normal text-gray-500 ml-2">
                  (Optional but recommended)
                </span>
              </label>
              
              {/* Upload Area */}
              <div 
                onClick={() => !isSubmitting && fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 ${
                  isDragging 
                    ? 'border-blue-400 bg-blue-50' 
                    : isSubmitting 
                    ? 'cursor-not-allowed opacity-50 border-gray-300' 
                    : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50 cursor-pointer'
                }`}
              >
                <Upload className={`mx-auto h-8 w-8 ${isDragging ? 'text-blue-500' : 'text-gray-400'}`} />
                <p className="mt-2 text-sm text-gray-600">
                  <span className={`font-medium ${isDragging ? 'text-blue-600' : 'text-blue-600 hover:text-blue-700'}`}>
                    {isDragging ? 'Drop files here' : 'Click to upload'}
                  </span>
                  {!isDragging && (
                    <>
                      {' '}or drag and drop
                    </>
                  )}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Screenshots, logs, documents (PNG, JPG, PDF, DOC, XLS)
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  Maximum file size: 10MB per file, up to 5 files total
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileInputChange}
                  className="hidden"
                  accept="image/*,.pdf,.txt,.doc,.docx,.xls,.xlsx,.csv,.json,.xml"
                  disabled={isSubmitting}
                />
              </div>

              {/* Uploaded Files */}
              {uploadedFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-700">
                      Uploaded Files ({uploadedFiles.length}/5)
                    </h4>
                    <button
                      type="button"
                      onClick={() => {
                        if (!isSubmitting) {
                          uploadedFiles.forEach(file => {
                            if (file.preview) {
                              URL.revokeObjectURL(file.preview);
                            }
                          });
                          setUploadedFiles([]);
                        }
                      }}
                      disabled={isSubmitting}
                      className="text-xs text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Remove all
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {uploadedFiles.map((file, index) => (
                      <div key={file.id || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          {file.preview ? (
                            <img 
                              src={file.preview} 
                              alt={file.name}
                              className="w-10 h-10 object-cover rounded border"
                            />
                          ) : (
                            getFileIcon(file.type)
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate" title={file.name}>
                              {file.name}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <span>{formatFileSize(file.size)}</span>
                              <span>•</span>
                              <span className="capitalize">{file.type.split('/')[1]}</span>
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => !isSubmitting && removeFile(index)}
                          disabled={isSubmitting}
                          className="ml-2 p-1 text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {validationErrors.attachments && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.attachments}</p>
              )}
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => {
                const hasContent = formData.title || formData.description || uploadedFiles.length > 0 || selectedCustomer;
                if (hasContent && !isSubmitting) {
                  if (window.confirm('Are you sure you want to cancel? Your changes will be lost.')) {
                    navigateBack();
                  }
                } else if (!isSubmitting) {
                  navigateBack();
                }
              }}
              disabled={isSubmitting}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={clearForm}
                disabled={isSubmitting}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Clear Form
              </button>
              
              <button
                type="submit"
                disabled={
                  isSubmitting || 
                  !formData.title.trim() || 
                  !formData.description.trim() || 
                  !formData.category_id ||
                  (isOnBehalfMode && (!selectedCustomer || !formData.reason_for_behalf.trim())) ||
                  uploadedFiles.length > 5
                }
                className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2 min-w-[140px] justify-center"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    {isOnBehalfMode ? 'Create on Behalf' : 'Create Ticket'}
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Footer Information */}
      <div className="mt-6 text-center text-xs text-gray-500 space-y-2">
        <p>
          By creating a ticket{isOnBehalfMode ? ' on behalf of a customer' : ''}, 
          you agree to our support terms and conditions.
        </p>
        <p>
          Need immediate help? Call our support hotline at{' '}
          <a href="tel:+1234567890" className="text-blue-600 hover:text-blue-700 font-medium">
            +1 (234) 567-890
          </a>
        </p>
        {isOnBehalfMode && (
          <div className="inline-flex items-center gap-2 mt-2 text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-3 py-1">
            <AlertTriangle className="h-4 w-4" />
            <span>Ensure you have the customer's permission before creating tickets on their behalf</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateTicket;